import type { ReactNode } from 'react'
import { CmsLink } from '@/components/CmsLink'

/**
 * A link an editor wrote inside a line of copy, without letting HTML in.
 *
 * A consent box has to be able to say "I agree to the [privacy
 * policy](/privacy)" — a checkbox that names a document nobody can open is a
 * worse question than one that does not name it at all. The label is a string
 * in the payload and it stays one: what is stored is the text an editor typed,
 * and the anchor is built here, from a shape this file recognises. Storing
 * markup instead would mean serving whatever anyone with panel access ever
 * pasted, straight into the page.
 *
 * Only the two-bracket form is a link, and only to somewhere a link may go.
 * Anything else — a phone number in brackets, a scheme nobody should be
 * following — is left exactly as it was written and rendered as text, which
 * React escapes for us.
 */
const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g

/** Where an editor's link is allowed to point. */
function isAllowed(href: string): boolean {
    // A path, or one of the two schemes a form's copy has any business naming.
    // `//host` looks like a path and is not one.
    return (
        (href.startsWith('/') && !href.startsWith('//')) ||
        href.startsWith('https://') ||
        href.startsWith('mailto:')
    )
}

/**
 * Whether this line carries a link at all.
 *
 * A caller needs to know before it renders: a link inside a `<label>` is a
 * link that ticks the checkbox it is describing, because a click anywhere in a
 * label activates its control. Stopping the event does not help — React
 * listens at the root, by which time the label has already seen the click — so
 * the fix is structural, and the caller has to make it.
 */
export function hasLink(text: string): boolean {
    return [...text.matchAll(LINK)].some((match) => isAllowed(match[2]))
}

export function withLinks(text: string): ReactNode {
    const parts: ReactNode[] = []
    let read = 0

    for (const match of text.matchAll(LINK)) {
        const [whole, label, href] = match

        if (!isAllowed(href)) {
            continue
        }

        parts.push(text.slice(read, match.index))
        parts.push(
            <CmsLink
                key={`${match.index}-${href}`}
                href={href}
                target={href.startsWith('https://') ? '_blank' : undefined}
                className="text-fg underline underline-offset-4 transition-colors duration-200 hover:text-accent"
            >
                {label}
            </CmsLink>,
        )
        read = (match.index ?? 0) + whole.length
    }

    if (parts.length === 0) {
        return text
    }

    parts.push(text.slice(read))

    return parts
}
